/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue, no-promise-executor-return, consistent-return, no-void, max-len, class-methods-use-this, no-underscore-dangle, no-undef, no-plusplus, prefer-destructuring, no-param-reassign, no-use-before-define, no-nested-ternary, max-classes-per-file, no-unused-vars */
// Ported from newdtrader (packages/core/src/_common/base/dfunded); kept in its
// original style so fixes can be carried across the two copies.
/**
 * Market data for a DFunded session, straight from Deriv's public socket.
 *
 * Ticks, the product catalog and price quotes need no login, so the browser
 * asks Deriv for them directly and as real streams. The funded backend is left
 * with orders and their results, which keeps buys off a socket shared with
 * everyone's market data and spends none of the firm account's request
 * allowance. Callers fall back to the backend when this feed cannot answer.
 *
 * Kept in step with multi_platforms src/services/dfunded-public-feed.ts.
 */
const DERIV_PUBLIC_WS_URL = 'wss://api.derivws.com/trading/v1/options/ws/public';
/** The feed itself failed (socket, timeout, empty catalog): worth asking the backend instead. */
class PublicFeedUnavailable extends Error {
    constructor(code) {
        super(code);
        this.code = code;
    }
}
/** Deriv answered with an error of its own; the backend would only hear the same. */
class PublicFeedRejection extends Error {
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
const PROPOSAL_KEYS = new Set([
    'amount',
    'basis',
    'contract_type',
    'currency',
    'duration',
    'duration_unit',
    'barrier',
    'barrier2',
    'date_expiry',
    'date_start',
    'expiry_type',
    'growth_rate',
    'limit_order',
    'multiplier',
    'product_type',
    'selected_tick',
]);
const SYMBOL = /^[\w-]{1,30}$/;
/**
 * Turn an app (legacy-format) request into the strict v2 request the public
 * socket accepts. Returns null for anything that is not plain market data.
 */
const shapePublicRequest = request => {
    if (!request || typeof request !== 'object') return null;
    if (request.active_symbols) return { active_symbols: request.active_symbols === 'full' ? 'full' : 'brief' };
    if (request.contracts_for) {
        return typeof request.contracts_for === 'string' && SYMBOL.test(request.contracts_for)
            ? { contracts_for: request.contracts_for }
            : null;
    }
    if (request.trading_times) {
        const date = String(request.trading_times);
        return /^\d{4}-\d{2}-\d{2}$/.test(date) ? { trading_times: date } : null;
    }
    if (request.proposal) {
        const shaped = { proposal: 1 };
        for (const [key, value] of Object.entries(request)) if (PROPOSAL_KEYS.has(key)) shaped[key] = value;
        const symbol = request.symbol ?? request.underlying_symbol;
        if (typeof symbol !== 'string' || !SYMBOL.test(symbol)) return null;
        shaped.underlying_symbol = symbol;
        const amount = Number(shaped.amount);
        if (!Number.isFinite(amount) || amount <= 0) return null;
        shaped.amount = amount;
        return shaped;
    }
    return null;
};
const CATALOG_TTL_MS = {
    active_symbols: 5 * 60000,
    contracts_for : 5 * 60000,
    trading_times : 60 * 60000,
};
const catalogKind = shaped => Object.keys(CATALOG_TTL_MS).find(kind => kind in shaped) || null;
/** Deriv allows 100 subscriptions a socket; quotes are recycled well before that. */
const MAX_QUOTE_STREAMS = 40;
const REQUEST_TIMEOUT_MS = 8000;
const KEEPALIVE_MS = 25000;
class DFundedPublicFeed {
    constructor(createSocket = () => new WebSocket(DERIV_PUBLIC_WS_URL)) {
        this.createSocket = createSocket;
        this.socket = null;
        this.opening = null;
        this.nextReqId = 1;
        this.pending = new Map();
        this.ticks = new Map();
        this.quotes = new Map();
        this.streamByReqId = new Map();
        this.catalog = new Map();
        this.catalogInFlight = new Map();
        this.keepAlive = null;
        this.reconnectTimer = null;
        this.reconnectDelay = 500;
        this.nextQuoteKey = 1;
        this.closed = false;
    }

    /** Ms since the last streamed tick for the symbol (Infinity when none yet). */
    tickAge(symbol) {
        const stream = this.ticks.get(symbol);
        return stream?.lastAt ? Date.now() - stream.lastAt : Infinity;
    }

    /** One-shot market request. Catalog answers are cached; identical ones in flight are shared. */
    async request(shaped) {
        const kind = catalogKind(shaped);
        if (!kind) return this.call(shaped);
        const key = JSON.stringify(shaped);
        const hit = this.catalog.get(key);
        if (hit && hit.expires > Date.now()) return hit.response;
        const running = this.catalogInFlight.get(key);
        if (running) return running;
        const task = this.call(shaped)
            .then(response => {
                // An empty catalog is how Deriv says "not served here"; let the backend try.
                if (kind === 'active_symbols' && !response?.active_symbols?.length) {
                    throw new PublicFeedUnavailable('PUBLIC_FEED_EMPTY_CATALOG');
                }
                this.catalog.set(key, { expires: Date.now() + CATALOG_TTL_MS[kind], response });
                return response;
            })
            .finally(() => this.catalogInFlight.delete(key));
        this.catalogInFlight.set(key, task);
        return task;
    }

    /** Live ticks for a symbol: one upstream subscription however many listeners. */
    watchTicks(symbol, listener) {
        let stream = this.ticks.get(symbol);
        if (!stream) {
            stream = { listeners: new Set(), subscriptionId: null, reqId: null, lastAt: 0, subscribing: false };
            this.ticks.set(symbol, stream);
            this.subscribeTicks(symbol, stream);
        }
        stream.listeners.add(listener);
        return () => {
            const current = this.ticks.get(symbol);
            if (!current) return;
            current.listeners.delete(listener);
            if (current.listeners.size) return;
            this.ticks.delete(symbol);
            this.forget(current);
        };
    }

    /**
     * A streamed quote. Resolves with Deriv's first answer; later prices go to
     * `listener` until `stop()`. Rejects (and streams nothing) if Deriv refuses.
     */
    async streamQuote(shaped, listener) {
        if (this.quotes.size >= MAX_QUOTE_STREAMS) {
            const oldest = this.quotes.keys().next().value;
            this.stopQuote(oldest);
        }
        const key = `q${this.nextQuoteKey++}`;
        const stream = { shaped, listener, subscriptionId: null, reqId: null, subscribing: false };
        this.quotes.set(key, stream);
        try {
            const first = await this.subscribeQuote(key, stream);
            return { first, stop: () => this.stopQuote(key) };
        } catch (error) {
            this.stopQuote(key);
            throw error;
        }
    }

    close() {
        this.closed = true;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        if (this.keepAlive) clearInterval(this.keepAlive);
        this.ticks.clear();
        this.quotes.clear();
        this.streamByReqId.clear();
        this.failPending('PUBLIC_FEED_CLOSED');
        try {
            this.socket?.close();
        } catch {
            /* already closed */
        }
        this.socket = null;
    }

    // ── internals ──
    stopQuote(key) {
        const stream = this.quotes.get(key);
        if (!stream) return;
        this.quotes.delete(key);
        this.forget(stream);
    }

    forget(stream) {
        if (stream.reqId !== null) this.streamByReqId.delete(stream.reqId);
        if (stream.subscriptionId && this.socket?.readyState === WebSocket.OPEN) {
            this.call({ forget: stream.subscriptionId }).catch(() => undefined);
        }
    }

    subscribeTicks(symbol, stream) {
        stream.subscribing = true;
        this.call({ ticks: symbol, subscribe: 1 }, reqId => {
            stream.reqId = reqId;
            this.streamByReqId.set(reqId, { kind: 'tick', symbol });
        })
            .then(message => {
                stream.subscriptionId = message?.subscription?.id || null;
                // Unwatched while the request was in flight.
                if (this.ticks.get(symbol) !== stream) this.forget(stream);
            })
            .catch(() => undefined) // the caller watches tickAge() and falls back
            .finally(() => {
                stream.subscribing = false;
            });
    }

    async subscribeQuote(key, stream) {
        stream.subscribing = true;
        const message = await this.call({ ...stream.shaped, subscribe: 1 }, reqId => {
            stream.reqId = reqId;
            this.streamByReqId.set(reqId, { kind: 'quote', key });
        }).finally(() => {
            stream.subscribing = false;
        });
        stream.subscriptionId = message?.subscription?.id || null;
        if (this.quotes.get(key) !== stream) this.forget(stream);
        return message;
    }

    open() {
        if (this.socket?.readyState === WebSocket.OPEN) return Promise.resolve(this.socket);
        if (this.opening) return this.opening;
        this.closed = false;
        this.opening = new Promise((resolve, reject) => {
            let socket;
            try {
                socket = this.createSocket();
            } catch {
                reject(new PublicFeedUnavailable('PUBLIC_FEED_CONNECT_FAILED'));
                return;
            }
            this.socket = socket;
            const timer = setTimeout(() => {
                reject(new PublicFeedUnavailable('PUBLIC_FEED_CONNECT_TIMEOUT'));
                try {
                    socket.close();
                } catch {
                    /* never opened */
                }
            }, REQUEST_TIMEOUT_MS);
            socket.addEventListener('open', () => {
                clearTimeout(timer);
                this.reconnectDelay = 500;
                this.startKeepAlive();
                resolve(socket);
            });
            socket.addEventListener('message', event => this.onMessage(event));
            socket.addEventListener('close', () => {
                clearTimeout(timer);
                if (this.socket !== socket) return;
                this.socket = null;
                this.failPending('PUBLIC_FEED_DISCONNECTED');
                reject(new PublicFeedUnavailable('PUBLIC_FEED_DISCONNECTED'));
                this.scheduleReconnect();
            });
            socket.addEventListener('error', () => {
                /* a close event follows */
            });
        }).finally(() => {
            this.opening = null;
        });
        // Streams that were live before a drop come back with the socket.
        this.opening.then(() => this.resubscribe()).catch(() => undefined);
        return this.opening;
    }

    resubscribe() {
        // A stream whose own first request is still waiting for this socket to
        // open subscribes itself; doing it here too would subscribe it twice.
        const waiting = stream => stream.subscribing && stream.reqId === null;
        for (const [symbol, stream] of this.ticks) {
            if (waiting(stream)) continue;
            if (stream.reqId !== null) this.streamByReqId.delete(stream.reqId);
            stream.subscriptionId = null;
            stream.reqId = null;
            this.subscribeTicks(symbol, stream);
        }
        for (const [key, stream] of this.quotes) {
            if (waiting(stream)) continue;
            if (stream.reqId !== null) this.streamByReqId.delete(stream.reqId);
            stream.subscriptionId = null;
            stream.reqId = null;
            this.subscribeQuote(key, stream)
                .then(message => stream.listener(message))
                .catch(() => this.stopQuote(key));
        }
    }

    scheduleReconnect() {
        if (this.closed || this.reconnectTimer) return;
        if (!this.ticks.size && !this.quotes.size) return; // nothing live; the next request reopens it
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.open().catch(() => this.scheduleReconnect());
        }, this.reconnectDelay);
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 8000);
    }

    startKeepAlive() {
        if (this.keepAlive) clearInterval(this.keepAlive);
        this.keepAlive = setInterval(() => {
            if (this.socket?.readyState !== WebSocket.OPEN) return;
            this.call({ ping: 1 }).catch(error => {
                // No pong: the socket is dead but has not noticed. Replace it.
                if (error instanceof PublicFeedUnavailable && error.code === 'PUBLIC_FEED_TIMEOUT') {this.socket?.close();}
            });
        }, KEEPALIVE_MS);
    }

    async call(payload, onSend) {
        const socket = await this.open();
        const reqId = this.nextReqId++;
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                if (this.pending.delete(reqId)) reject(new PublicFeedUnavailable('PUBLIC_FEED_TIMEOUT'));
            }, REQUEST_TIMEOUT_MS);
            this.pending.set(reqId, { resolve, reject, timer });
            onSend?.(reqId);
            try {
                socket.send(JSON.stringify({ ...payload, req_id: reqId }));
            } catch {
                clearTimeout(timer);
                this.pending.delete(reqId);
                reject(new PublicFeedUnavailable('PUBLIC_FEED_DISCONNECTED'));
            }
        });
    }

    failPending(code) {
        for (const pending of this.pending.values()) {
            clearTimeout(pending.timer);
            pending.reject(new PublicFeedUnavailable(code));
        }
        this.pending.clear();
    }

    onMessage(event) {
        let message;
        try {
            message = JSON.parse(String(event.data));
        } catch {
            return;
        }
        const reqId = Number(message.req_id || 0);
        const pending = this.pending.get(reqId);
        if (pending) {
            this.pending.delete(reqId);
            clearTimeout(pending.timer);
            if (message.error) {
                this.streamByReqId.delete(reqId);
                const code = String(message.error.code || 'DERIV_ERROR');
                // Out of allowance on this socket: a fresh one has a fresh allowance.
                if (code === 'RateLimit') {
                    pending.reject(new PublicFeedUnavailable('PUBLIC_FEED_RATE_LIMITED'));
                    this.socket?.close();
                } else pending.reject(new PublicFeedRejection(code, String(message.error.message || code)));
                return;
            }
            pending.resolve(message);
            if (message.msg_type !== 'tick') return; // a quote's first answer goes to the caller
        }
        const target = this.streamByReqId.get(reqId);
        if (!target || message.error) return;
        if (target.kind === 'tick') {
            const stream = this.ticks.get(target.symbol);
            if (!stream || !message.tick) return;
            stream.lastAt = Date.now();
            for (const listener of stream.listeners) listener(message.tick);
        } else if (message.proposal) {
            this.quotes.get(target.key)?.listener(message);
        }
    }
}
let shared = null;
const getDFundedPublicFeed = () => shared || (shared = new DFundedPublicFeed());
const closeDFundedPublicFeed = () => {
    shared?.close();
    shared = null;
};

module.exports = {
    DERIV_PUBLIC_WS_URL,
    DFundedPublicFeed,
    PublicFeedRejection,
    PublicFeedUnavailable,
    closeDFundedPublicFeed,
    getDFundedPublicFeed,
    shapePublicRequest,
};
